// Cross-page navigation. On an ACT site every game has its own subdomain
// (ACT HQ DR-0017; Theo, 28 Sep 2026): the hub is play.actuniverse.com and
// Firestrike firestrike.actuniverse.com, one sign-in shared through a site-wide
// cookie (siteStorage.ts). Anywhere else (dev, *.pages.dev previews) the old
// one-origin paths stay (DR-0045 D3, DR-0047): the hub at "/", Firestrike at
// "/firestrike/", which the hub dev server proxies to the game's dev server.
//
// Import-light on purpose: the games' feather entries use it before the engine boots.
import { onSiteHost } from "./siteStorage";

const ON_SITE = onSiteHost();

export const HUB_PAGE_BASE = ON_SITE ? "https://play.actuniverse.com/" : "/";
export const FIRESTRIKE_PAGE_BASE = ON_SITE ? "https://firestrike.actuniverse.com/" : "/firestrike/";
/** Ludus is its own app (devaux2/act-ludus), not served by the hub, so it has one address everywhere. */
export const LUDUS_PAGE_BASE = "https://ludus.actuniverse.com/";
/** Vector Drift is its own site (devaux2/driftracer): one address everywhere too. */
export const VECTOR_DRIFT_PAGE_BASE = "https://vectordrift.actuniverse.com/";
/** Ground War is its own site (devaux2/act-groundwar), opening on its menu: one address everywhere too. */
export const GROUND_WAR_PAGE_BASE = "https://groundwar.actuniverse.com/";

/** Every page of the site by name, for "go to <page>" links (the router sends
 *  a moved game's old path to the hub as `/?go=<name>`). */
export const SITE_PAGES: Readonly<Record<string, string>> = {
  hub: HUB_PAGE_BASE,
  firestrike: FIRESTRIKE_PAGE_BASE,
  ludus: LUDUS_PAGE_BASE,
  vectordrift: VECTOR_DRIFT_PAGE_BASE,
  groundwar: GROUND_WAR_PAGE_BASE,
};

/** URL for a route on the hub page, e.g. hubPageUrl("equip") → "/#/equip". */
export function hubPageUrl(route = ""): string {
  return route ? `${HUB_PAGE_BASE}#/${route}` : HUB_PAGE_BASE;
}

/** URL for a route on the Firestrike page. `query` rides inside the hash —
 *  the existing convention (`#/wave-solo?ai=zombie`; scene names stay wave-*). */
export function firestrikePageUrl(route = "", query = ""): string {
  const q = query ? (query.startsWith("?") ? query : `?${query}`) : "";
  return route ? `${FIRESTRIKE_PAGE_BASE}#/${route}${q}` : FIRESTRIKE_PAGE_BASE;
}

/** Navigate to a hub-page route. When already ON the hub page this is only a
 *  hash change (no reload); from any other page it's a real navigation. */
export function gotoHubPage(route = ""): void {
  location.href = hubPageUrl(route);
}

/** Navigate to a Firestrike-page route (same hash-change-when-already-there rule). */
export function gotoFirestrikePage(route = "", query = ""): void {
  location.href = firestrikePageUrl(route, query);
}
